import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/admin/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ROLES = [["super_admin", "Super Admin"], ["admin", "Administrator"], ["property_manager", "Property Manager"], ["enquiry_manager", "Enquiry Manager"], ["content_editor", "Content Editor"], ["read_only", "Read Only"]];

export default function AdminUsers() {
  const [items, setItems] = useState(null);
  const [add, setAdd] = useState(null);
  const load = () => api.get("/admin/users").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!add.email || !add.password) { toast.error("Email and password required"); return; }
    try { await api.post("/admin/users", add); toast.success("User created"); setAdd(null); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/admin/users/${id}`); toast.success("Deleted"); load(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  const roleName = (r) => ROLES.find(([v]) => v === r)?.[1] || r;

  return (
    <div>
      <PageHeader title="Admin users" subtitle="Manage administrator accounts and roles"
        action={<Button onClick={() => setAdd({ role: "read_only" })} className="rounded-full bg-[#C81D35] hover:bg-[#a5162b] gap-2" data-testid="add-user-btn"><Plus className="w-4 h-4" /> Add user</Button>} />
      <div className="bg-white rounded-xl border border-border overflow-hidden">
        {items === null ? <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 text-[#C81D35] animate-spin" /></div>
          : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-muted-foreground border-b border-border"><th className="p-4 font-medium">Name</th><th className="p-4 font-medium">Email</th><th className="p-4 font-medium">Role</th><th className="p-4"></th></tr></thead>
            <tbody>
              {items.map((u) => (
                <tr key={u.id} className="border-b border-border/60 last:border-0" data-testid={`user-${u.id}`}>
                  <td className="p-4 font-medium text-[#111827]">{u.name || "—"}</td>
                  <td className="p-4 text-muted-foreground">{u.email}</td>
                  <td className="p-4"><span className="text-xs px-2 py-1 rounded-full bg-muted">{roleName(u.role)}</span></td>
                  <td className="p-4 text-right">{u.role !== "super_admin" && <Button variant="ghost" size="icon" className="text-red-600" onClick={() => del(u.id)}><Trash2 className="w-4 h-4" /></Button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!add} onOpenChange={(o) => !o && setAdd(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add admin user</DialogTitle></DialogHeader>
          {add && (
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Name</Label><Input value={add.name || ""} onChange={(e) => setAdd({ ...add, name: e.target.value })} data-testid="user-name" /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={add.email || ""} onChange={(e) => setAdd({ ...add, email: e.target.value })} data-testid="user-email" /></div>
              <div className="space-y-1.5"><Label>Password</Label><Input type="password" value={add.password || ""} onChange={(e) => setAdd({ ...add, password: e.target.value })} data-testid="user-password" /></div>
              <div className="space-y-1.5"><Label>Role</Label>
                <Select value={add.role} onValueChange={(v) => setAdd({ ...add, role: v })}><SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger><SelectContent>{ROLES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
          )}
          <DialogFooter><Button variant="outline" onClick={() => setAdd(null)} className="rounded-full">Cancel</Button><Button onClick={create} className="rounded-full bg-[#C81D35] hover:bg-[#a5162b]" data-testid="save-user">Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
